import cx from "classnames"
import React, { useEffect, useMemo, useState } from "react"
import AntdModal from "./AntdModal"
import { Checkbox, Collapse, Segmented, Timeline } from "antd-v5"
import { CaretRightOutlined, CheckCircleOutlined, CloseCircleOutlined, FieldTimeOutlined } from "@ant-design/icons"
import { DATE_FORMATS, GOOGLE_MAPS_API_VERSION, ShiftStatus } from "config/constants"
import { momentZ } from "config/moment"
import { PreviousShiftEvent } from "@type/workMonitor.types"
import AntdEmpty from "./AntdEmpty"
import Icon from "./Icon"
import randomColor from "randomcolor"
import AntdButton from "./AntdButton"
import ShiftActivityMapVisualization, { MapConfig } from "./ShiftActivityMapVisualization"
import { APIProvider } from "@vis.gl/react-google-maps"
import env from "config/env"
import { twMerge } from "tailwind-merge"

const { Panel } = Collapse

const libraries = ["places", "visualization", "drawing", "geometry"]

// Server sends GeoJSON ordering: [longitude, latitude]
const getPosition = (geo?: { coordinates?: number[] }) => ({
	lat: geo?.coordinates?.[1] ?? 0,
	lng: geo?.coordinates?.[0] ?? 0,
})

interface ShiftActivityVisualizationProps {
	shiftActivity: PreviousShiftEvent[]
}

const ShiftActivityVisualization: React.FC<ShiftActivityVisualizationProps> = ({ shiftActivity }) => {
	const [selectedMarker, setSelectedMarker] = useState<{ [key: string]: string } | null>(null)
	const [selectedCall, setSelectedCall] = useState<Partial<ShiftStatus>>(ShiftStatus.CHECK)
	const [disableBox, setDisableBox] = useState(false)

	const {
		bookOnCall,
		checkCalls,
		timelyScannedCheckCalls,
		inventoryCalls,
		timelyScannedInventoryCalls,
		qrCalls,
		bookOffCall,
	} = useMemo(() => {
		const bookOnCall: PreviousShiftEvent[] = []
		const checkCalls: PreviousShiftEvent[] = []
		const inventoryCalls: PreviousShiftEvent[] = []
		const qrCalls: PreviousShiftEvent[] = []
		const bookOffCall: PreviousShiftEvent[] = []

		shiftActivity?.forEach(call => {
			const { status } = call
			if (status === ShiftStatus.BOOKON) bookOnCall.push(call as any)
			if (status === ShiftStatus.CHECK) checkCalls.push(call as any)
			if (status === ShiftStatus.INVENTORY) inventoryCalls.push(call as any)
			if (status === ShiftStatus.QR) qrCalls.push(call as any)
			if (status === ShiftStatus.BOOKOFF) bookOffCall.push(call as any)
		})

		const timelyScannedCheckCalls = checkCalls.filter(({ isEventMissed }) => !isEventMissed)
		const timelyScannedInventoryCalls = inventoryCalls.filter(({ isEventMissed }) => !isEventMissed)

		return {
			bookOnCall,
			checkCalls,
			timelyScannedCheckCalls,
			inventoryCalls,
			timelyScannedInventoryCalls,
			qrCalls,
			bookOffCall,
		}
	}, [shiftActivity])

	useEffect(() => {
		if (selectedCall === ShiftStatus.QR && qrCalls.length) {
			setSelectedMarker({ [qrCalls?.[0]?.dueTime]: "" })
		}
	}, [qrCalls, selectedCall])

	const markers = useMemo(() => {
		if (selectedCall === ShiftStatus.QR) {
			return qrCalls.flatMap(({ scannedQREvents = [], user }) => {
				return scannedQREvents.map(({ _id, geo, images = [], eventTime, dueTime, status, qr }) => {
					const color = randomColor({ format: "hex" })
					const name = qr?.name ?? ""

					return {
						_id,
						name,
						images,
						status,
						eventTime,
						dueTime,
						color,
						employee: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
						position: getPosition(geo),
					}
				})
			})
		}

		const selectTimelyScannedCalls: Partial<Record<ShiftStatus, PreviousShiftEvent[]>> = {
			[ShiftStatus.CHECK]: timelyScannedCheckCalls,
			[ShiftStatus.INVENTORY]: timelyScannedInventoryCalls,
		}

		const selectedCallList = selectTimelyScannedCalls?.[selectedCall] ?? []

		return (selectedCallList?.length ? [...bookOnCall, ...selectedCallList] : []).map(
			({ _id = "", geo, images = [], status, eventTime, dueTime, user }) => {
				const color = randomColor({ format: "hex", luminosity: "dark" })

				return {
					_id,
					status,
					images,
					eventTime,
					dueTime,
					color,
					employee: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
					position: getPosition(geo),
				}
			},
		)
	}, [selectedCall, bookOnCall, timelyScannedCheckCalls, timelyScannedInventoryCalls, qrCalls, bookOffCall])

	const allowedMarkers = useMemo(() => {
		if (selectedCall === ShiftStatus.QR) {
			return markers.filter(({ dueTime }) => dueTime in (selectedMarker ?? {}))
		} else return markers
	}, [markers, selectedCall, selectedMarker])

	const { heatMap, polylines } = useMemo(() => {
		const heatMap = qrCalls.flatMap(({ scannedQREvents = [] }) =>
			scannedQREvents.map(({ geo }) => {
				return {
					position: getPosition(geo),
				}
			}),
		)

		const polylines = qrCalls
			.filter(({ dueTime }) => dueTime in (selectedMarker ?? {}))
			.map(({ dueTime, status, scannedQREvents = [] }) => {
				const lineInfo = scannedQREvents.map(({ _id, geo, images = [], qr }) => {
					const name = qr?.name ?? ""
					return {
						_id,
						name,
						images,
						position: getPosition(geo),
					}
				})

				return {
					status,
					dueTime,
					lineInfo,
				}
			})

		return {
			heatMap,
			polylines,
		}
	}, [selectedCall, selectedMarker, qrCalls])

	const shiftStatusToIconMap: Record<string, (isEventMissed?: boolean) => { color: string; icon: React.ReactNode }> =
		{
			[ShiftStatus.BOOKON]: () => ({
				color: "blue",
				// icon: <AlertOutlined className="pl-1" />,
				icon: <span className="rounded-sm border border-[#1677ff] text-sm">ON</span>,
			}),
			[ShiftStatus.CHECK]: isEventMissed => ({
				color: isEventMissed ? "red" : "green",
				icon: isEventMissed ? (
					<CloseCircleOutlined className="pl-1" />
				) : (
					<CheckCircleOutlined className="pl-1" />
				),
			}),
			[ShiftStatus.INVENTORY]: isEventMissed => ({
				color: isEventMissed ? "red" : "green",
				icon: isEventMissed ? (
					<CloseCircleOutlined className="pl-1" />
				) : (
					<CheckCircleOutlined className="pl-1" />
				),
			}),
			[ShiftStatus.QR]: isEventMissed => ({
				color: isEventMissed ? "red" : "green",
				icon: isEventMissed ? (
					<CloseCircleOutlined className="pl-1" />
				) : (
					<CheckCircleOutlined className="pl-1" />
				),
			}),
			[ShiftStatus.BOOKOFF]: () => ({
				color: "green",
				icon: <FieldTimeOutlined className="pl-1" />,
			}),
		}

	const handleMapConfigChange = (val: MapConfig) => {
		if (val == MapConfig.HEATMAP) {
			setDisableBox(true)
			const selectAllMarkersPayload = [...bookOnCall, ...qrCalls, ...bookOffCall].reduce((acc, cur) => {
				return {
					...acc,
					[cur.dueTime]: "",
				}
			}, {})

			setSelectedMarker(selectAllMarkersPayload)
		} else {
			setDisableBox(false)
			if (qrCalls.length) setSelectedMarker({ [qrCalls?.[0]?.dueTime]: "" })
			else setSelectedMarker(null)
		}
	}

	return (
		<div className="flex h-full w-full">
			<div className="w-[300px]">
				<div className="pb-4">
					<Segmented
						value={selectedCall}
						options={[
							{ label: "CHECK", value: ShiftStatus.CHECK, className: "w-20" },
							{ label: "INVENTORY", value: ShiftStatus.INVENTORY, className: "w-28" },
							{ label: "QR", value: ShiftStatus.QR, className: "w-20" },
						]}
						onChange={val => {
							setSelectedCall(val as ShiftStatus)
							setSelectedMarker(null)
						}}
					/>
				</div>
				<div className="schedule-scrollbar h-[60vh] overflow-y-auto overflow-x-hidden py-3">
					{selectedCall === ShiftStatus.CHECK && (
						<div className="h-full">
							{checkCalls.length ? (
								<Timeline
									mode="left"
									items={[...bookOnCall, ...checkCalls, ...bookOffCall].map(
										({ _id = "", status, isEventMissed = false, dueTime, eventTime }) => ({
											dot: shiftStatusToIconMap[status](isEventMissed).icon,
											color: shiftStatusToIconMap[status](isEventMissed).color,
											label: momentZ(dueTime).format(DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm),
											children: (
												<div
													className={cx("flex space-x-2", {
														"cursor-pointer":
															(status === ShiftStatus.CHECK ||
																status === ShiftStatus.BOOKON) &&
															!isEventMissed,
													})}
													onClick={() => {
														if (
															(status === ShiftStatus.CHECK ||
																status === ShiftStatus.BOOKON) &&
															!isEventMissed
														)
															setSelectedMarker({ [dueTime]: _id })
													}}
												>
													<div>
														{isEventMissed
															? `N/A`
															: `${momentZ(eventTime).format(DATE_FORMATS.HH_mm)}`}
													</div>
													{selectedMarker?.[dueTime] === _id && (
														<div className="full h-2 w-2 rounded">
															<Icon name="map" color="purple" size={20} />
														</div>
													)}
												</div>
											),
										}),
									)}
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<AntdEmpty description="No Check Calls" />
								</div>
							)}
						</div>
					)}
					{selectedCall === ShiftStatus.INVENTORY && (
						<div className="h-full">
							{inventoryCalls.length ? (
								<Timeline
									mode="left"
									items={[...bookOnCall, ...inventoryCalls, ...bookOffCall].map(
										({ _id = "", status, isEventMissed = false, dueTime, eventTime }) => ({
											dot: shiftStatusToIconMap[status](isEventMissed).icon,
											color: shiftStatusToIconMap[status](isEventMissed).color,
											label: momentZ(dueTime).format(DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm),
											children: (
												<div
													className={cx("flex space-x-2", {
														"cursor-pointer":
															(status === ShiftStatus.INVENTORY ||
																status === ShiftStatus.BOOKON) &&
															!isEventMissed,
													})}
													onClick={() => {
														if (
															(status === ShiftStatus.INVENTORY ||
																status === ShiftStatus.BOOKON) &&
															!isEventMissed
														)
															setSelectedMarker({ [dueTime]: _id })
													}}
												>
													<div>
														{isEventMissed
															? `N/A`
															: `${momentZ(eventTime).format(DATE_FORMATS.HH_mm)}`}
													</div>
													{selectedMarker?.[dueTime] === _id && (
														<div>
															<Icon name="map" color="info" size={20} />
														</div>
													)}
												</div>
											),
										}),
									)}
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<AntdEmpty description="No Inventory Calls" />
								</div>
							)}
						</div>
					)}
					{selectedCall === ShiftStatus.QR && (
						<div className="px-2">
							{qrCalls.length ? (
								<Timeline
									items={[...bookOnCall, ...qrCalls, ...bookOffCall].map(
										({
											_id = "",
											status,
											isEventMissed = false,
											dueTime,
											eventTime,
											scannedQREvents = [],
											missingQREvents = [],
										}) => ({
											dot: shiftStatusToIconMap[status](isEventMissed).icon,
											color: shiftStatusToIconMap[status](isEventMissed).color,

											children: (
												<div>
													{status === ShiftStatus.QR ? (
														<div className="flex flex-col space-x-2">
															<div
																className={twMerge(
																	cx("flex space-x-2", {
																		"pointer-events-none": disableBox,
																	}),
																)}
																onClick={() => {
																	if (disableBox) return
																	if (selectedMarker && dueTime in selectedMarker) {
																		const rest = Object.fromEntries(
																			Object.entries(selectedMarker).filter(
																				([key]) => key !== dueTime,
																			),
																		)
																		setSelectedMarker(rest)
																	} else {
																		setSelectedMarker({
																			...selectedMarker,
																			[dueTime]: "",
																		})
																	}
																}}
															>
																<div className="cursor-pointer">
																	{momentZ(dueTime).format(
																		DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm,
																	)}
																</div>
																<div>
																	<Checkbox
																		disabled={disableBox}
																		checked={dueTime in (selectedMarker ?? {})}
																		onClick={() =>
																			setSelectedMarker({ [dueTime]: "" })
																		}
																	/>
																</div>
															</div>
															<Collapse
																className=" !bg-white"
																bordered={false}
																expandIcon={({ isActive }) => (
																	<CaretRightOutlined rotate={isActive ? 90 : 0} />
																)}
															>
																<Panel
																	key={"Scanned QRs"}
																	header={
																		<div className="w-full cursor-cell">
																			Scanned QRs
																		</div>
																	}
																>
																	<div className="h-full w-full">
																		{[...scannedQREvents]
																			.sort(
																				(a, b) =>
																					new Date(a.eventTime).valueOf() -
																					new Date(b.eventTime).valueOf(),
																			)
																			.map(qrEvent => {
																				return (
																					<div
																						className="flex cursor-pointer justify-start space-x-2"
																						key={qrEvent._id}
																					>
																						<span>
																							{momentZ(
																								qrEvent.eventTime,
																							).format(
																								DATE_FORMATS.HH_mm,
																							)}{" "}
																							-
																						</span>
																						<span>{qrEvent.qr?.name}</span>
																					</div>
																				)
																			})}
																	</div>
																</Panel>
																<Panel
																	key={"Missed QRs"}
																	header={
																		<div className="w-full cursor-cell">
																			Missed QRs
																		</div>
																	}
																>
																	{missingQREvents.map(qrEvent => {
																		return (
																			<div key={qrEvent._id}>
																				<span>N/A - </span>
																				<span>{qrEvent.qr?.name}</span>
																			</div>
																		)
																	})}
																</Panel>
															</Collapse>
														</div>
													) : (
														<div className="flex cursor-pointer items-center space-x-2">
															<div onClick={() => setSelectedMarker({ dueTime: _id })}>
																{isEventMissed
																	? `${momentZ(dueTime).format(
																			DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm,
																	  )}`
																	: `${momentZ(eventTime).format(
																			DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm,
																	  )}`}
															</div>
															{selectedMarker?.[dueTime] === _id && (
																<div>
																	<Icon name="map" color="purple" size={20} />
																</div>
															)}
														</div>
													)}
												</div>
											),
										}),
									)}
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<AntdEmpty description="No QR Calls" />
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="w-[900px] pt-12">
				<APIProvider apiKey={env.googleMapsApiKey} version={GOOGLE_MAPS_API_VERSION} libraries={libraries}>
					<ShiftActivityMapVisualization
						markers={allowedMarkers}
						heatmap={heatMap}
						polyLines={polylines}
						selectedCall={selectedCall}
						selectedMarker={selectedMarker}
						setSelectedMarker={(dueTime = "", _id = "") => setSelectedMarker({ [dueTime]: _id })}
						legends={allowedMarkers.map(({ color, dueTime }) => ({
							color,
							title: momentZ(dueTime).format(DATE_FORMATS.HH_mm),
						}))}
						onMapConfigChange={handleMapConfigChange}
					/>
				</APIProvider>
			</div>

			{/* TODO ADD CHARTS FOR ANALYTICS SCANNED/NOT SCANNED QRs - Change the Width of Modal to 1400 */}
			{/* <div className="w-[200px]">hello</div> */}
		</div>
	)
}

const ShiftActivityVisualizationModal: React.FC<ShiftActivityVisualizationProps> = ({ shiftActivity }) => {
	const [openModal, setOpenModal] = useState(false)

	return (
		<div>
			<AntdButton icon={<Icon name="activity" color="black" size={20} />} onClick={() => setOpenModal(true)}>
				Visualize
			</AntdButton>

			{openModal && (
				<AntdModal
					open={openModal}
					width={1210}
					centered
					footer={null}
					title={<span>Shift Visualization</span>}
					onCancel={() => setOpenModal(false)}
				>
					{!shiftActivity.length ? (
						<div className="flex h-[62vh] w-full items-center justify-center">
							<AntdEmpty description="No Activity Found" />{" "}
						</div>
					) : (
						<ShiftActivityVisualization shiftActivity={shiftActivity} />
					)}
				</AntdModal>
			)}
		</div>
	)
}

export default ShiftActivityVisualizationModal
